import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { inr, periodLabel, type ID } from '@hrms/shared';
import { AutoGrid } from '../components/common';
import { api } from '../../../lib/api';
import { openFile } from '../../../lib/files';
import { useGo } from '../../../navigation/nav';
import { Alert, Button, Card, Icon, Loading, PageHeader, Row, Screen, Select, Stack, StatusBadge, Text, useTheme } from '../../../ui';
import { OUTPUTS } from '../components/outputs';

export function OutputsScreen() {
  const go = useGo();
  const { colors } = useTheme();
  const runs = useQuery({ queryKey: ['payroll', 'runs'], queryFn: () => api.payroll.runs() });
  const eligible = [...(runs.data ?? [])].filter((r) => r.status === 'processed' || r.status === 'locked' || r.status === 'paid').sort((a, b) => b.period.localeCompare(a.period));
  const [sel, setSel] = useState<ID | null>(null);
  const run = eligible.find((r) => r.id === sel) ?? eligible[0];
  if (runs.isLoading) return <Loading />;
  const locked = run && (run.status === 'locked' || run.status === 'paid');
  return (
    <Screen refreshing={runs.isRefetching} onRefresh={() => runs.refetch()} testID="payroll-outputs">
      <PageHeader title="Bank file & challans" subtitle="Bank and statutory files for a processed, locked or paid payroll run." />
      {!run ? (
        <Stack gap={12}>
          <Alert tone="info">No processed payroll runs yet. Process a run from Payroll runs first.</Alert>
          <Button icon="play" title="Go to payroll runs" onPress={() => go('PayrollRuns')} />
        </Stack>
      ) : (
        <>
          <Card>
            <Row justify="space-between" gap={12}>
              <Row gap={10}>
                <View style={{ width: 220 }}>
                  <Select label="Payroll run" value={run.id} onChange={setSel} options={eligible.map((r) => ({ value: r.id, label: periodLabel(r.period), description: r.status }))} testID="outputs-run" />
                </View>
                <View style={{ paddingTop: 22 }}>
                  <StatusBadge status={run.status} />
                </View>
              </Row>
              <Text variant="muted">{`${run.employee_count} employees · Net ${inr(run.total_net)}`}</Text>
            </Row>
          </Card>
          {!locked && <Alert tone="warning">This run is not locked yet. Only the payroll register is available; lock the run to download bank and statutory files.</Alert>}
          <AutoGrid min={260}>
            {OUTPUTS.map((o) => (
              <Card key={o.kind}>
                <Row align="flex-start" wrap={false} gap={12}>
                  <View style={{ width: 36, height: 36, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primarySoft }}>
                    <Icon name={o.icon} color={colors.primary} />
                  </View>
                  <View style={{ flex: 1, gap: 4 }}>
                    <Text weight="semibold">{o.title}</Text>
                    <Text variant="muted">{o.desc}</Text>
                    <Button icon="download" title="Download" disabled={o.needsLock && !locked} style={{ marginTop: 8 }} onPress={() => void openFile(api.payroll.outputPath(run.id, o.kind))} testID={`download-${o.kind}`} />
                  </View>
                </Row>
              </Card>
            ))}
          </AutoGrid>
        </>
      )}
    </Screen>
  );
}
